import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Mail, Globe, ChevronLeft, Eye, Building2, Navigation, ParkingCircle, ExternalLink, Send, X } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWdlbmNlbXNjIiwiYSI6ImNtbmN6NGN6ajEyNjYycnNoMnhoZnB2cm4ifQ.DR23BWxFiKaOpi3fHH06mQ';

const lensColors = {
  'Lentille Souple': { bg: '#dff6dd', text: '#2e7d32' },
  'Lentille rigide': { bg: '#e3f2fd', text: '#1565c0' },
  'Lentille sclérale': { bg: '#f3e5f5', text: '#7b1fa2' },
  'Orthokératologie': { bg: '#fff3e0', text: '#e65100' },
  'Kératocône': { bg: '#fce4ec', text: '#c62828' },
};

const PractitionerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parkings, setParkings] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ sender_name: '', sender_email: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/practitioners/${id}`)
      .then(r => setDoc(r.data))
      .catch(() => {
        axios.get(`${API}/directory/${id}`)
          .then(r => setDoc(r.data))
          .catch(() => navigate('/annuaire'));
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (doc && mapRef.current && !mapInstance.current) initMap();
  }, [doc]);

  // Fetch nearby parkings via Overpass API
  useEffect(() => {
    if (!doc) return;
    const lat = doc.lat || doc.latitude;
    const lng = doc.lng || doc.longitude;
    if (!lat || !lng) return;

    const radius = 500; // 500m radius
    const query = `[out:json][timeout:10];node["amenity"="parking"](around:${radius},${lat},${lng});out body 10;`;
    fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`)
      .then(r => r.json())
      .then(data => {
        const results = (data.elements || []).map(e => ({
          name: e.tags?.name || 'Parking',
          lat: e.lat,
          lng: e.lon,
          fee: e.tags?.fee,
          capacity: e.tags?.capacity,
        }));
        setParkings(results);
        if (mapInstance.current && results.length > 0) addParkingMarkers(results);
      })
      .catch(() => {});
  }, [doc]);

  const initMap = async () => {
    try {
      const mapboxgl = (await import('mapbox-gl')).default;
      mapboxgl.accessToken = MAPBOX_TOKEN;

      const lat = doc.lat || doc.latitude;
      const lng = doc.lng || doc.longitude;
      if (!lat || !lng) return;

      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [lng, lat],
        zoom: 15,
        language: 'fr',
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-left');

      map.on('load', () => {
        // French labels
        map.getStyle().layers.forEach(layer => {
          if (layer.layout?.['text-field']) {
            map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name_fr'], ['get', 'name']]);
          }
        });

        // Main practitioner marker
        const el = document.createElement('div');
        el.style.cssText = 'width:48px;height:48px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 12px rgba(0,0,0,.3);overflow:hidden';
        const img = document.createElement('img');
        img.src = doc.profile_image ? `${BACKEND_URL}${doc.profile_image}` : '/default-avatar-sfoalc.png';
        img.style.cssText = 'width:100%;height:100%;object-fit:cover';
        el.appendChild(img);

        new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
            `<div style="font-family:'Roboto',sans-serif;padding:4px"><p style="font-weight:700;font-size:14px;color:#1e3a5f;margin:0">${getName()}</p><p style="font-size:12px;color:#666;margin:4px 0 0">${getAddress()}</p></div>`
          ))
          .addTo(map);

        // Other locations
        (doc.other_locations || []).forEach(o => {
          if (!o.lat || !o.lng) return;
          const oEl = document.createElement('div');
          oEl.style.cssText = 'width:36px;height:36px;border-radius:50%;background:#e67e22;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;cursor:pointer';
          oEl.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
          new mapboxgl.Marker({ élément: oEl })
            .setLngLat([o.lng, o.lat])
            .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<div style="font-family:'Roboto',sans-serif"><p style="font-weight:700;color:#1e3a5f;margin:0">${o.name}</p><p style="font-size:12px;color:#666;margin:2px 0 0">${o.address || ''}</p><a href="/annuaire/${o.slug}" style="display:inline-block;margin-top:6px;padding:4px 12px;background:#e67e22;color:#fff;border-radius:20px;font-size:11px;font-weight:600;text-decoration:none">Voir la fiche</a></div>`
            ))
            .addTo(map);
        });

        mapInstance.current = map;

        // Add parking markers if already loaded
        if (parkings.length > 0) addParkingMarkers(parkings);
      });
    } catch (e) { console.error(e); }
  };

  const addParkingMarkers = async (parkingList) => {
    if (!mapInstance.current) return;
    const mapboxgl = (await import('mapbox-gl')).default;

    parkingList.forEach(p => {
      const el = document.createElement('div');
      el.style.cssText = 'width:28px;height:28px;border-radius:6px;background:#3b82f6;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;cursor:pointer';
      el.innerHTML = '<span style="color:#fff;font-weight:800;font-size:11px">P</span>';

      new mapboxgl.Marker({ élément: el })
        .setLngLat([p.lng, p.lat])
        .setPopup(new mapboxgl.Popup({ offset: 20, maxWidth: '220px' }).setHTML(
          `<div style="font-family:'Roboto',sans-serif"><p style="font-weight:700;color:#1e3a5f;font-size:13px;margin:0">${p.name}</p>${p.capacity ? `<p style="font-size:11px;color:#666;margin:2px 0 0">${p.capacity} places</p>` : ''}${p.fee === 'yes' ? '<p style="font-size:11px;color:#e67e22;margin:2px 0 0">Payant</p>' : p.fee === 'no' ? '<p style="font-size:11px;color:#27ae60;margin:2px 0 0">Gratuit</p>' : ''}<a href="https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}" target="_blank" style="display:inline-block;margin-top:6px;padding:3px 10px;background:#3b82f6;color:#fff;border-radius:16px;font-size:10px;font-weight:600;text-decoration:none">Itinéraire</a></div>`
        ))
        .addTo(mapInstance.current);
    });
  };

  const getName = () => doc?.name || `${doc?.first_name || ''} ${doc?.last_name || ''}`.trim();
  const getAddress = () => doc?.address || [doc?.street, doc?.postal_code, doc?.city].filter(Boolean).join(', ');
  const lensTypes = doc?.specialties || doc?.contact_lens_types || [];
  const otherLocations = doc?.other_locations || [];

  const getLat = () => doc?.lat || doc?.latitude;
  const getLng = () => doc?.lng || doc?.longitude;

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" />
    </div>
  );
  if (!doc) return null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="practitioner-profile">
      <link href="https://api.mapbox.com/mapbox-gl-js/v3.20.0/mapbox-gl.css" rel="stylesheet" />
      <SiteHeader />
      <SEO title={getName()} description={`${getName()}, ophtalmologiste adaptateur de lentilles de contact. Annuaire SFOALC.`} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 text-sm">
          <Link to="/annuaire" className="text-[#35AFE4] hover:underline flex items-center gap-1" data-testid="back-to-directory">
            <ChevronLeft size={16} /> Annuaire
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600" data-testid="breadcrumb-name">{getName()}</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full bg-white/20" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/10" />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14 relative z-10">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden shadow-xl flex-shrink-0" style={{ border: '3px solid rgba(255,255,255,0.3)' }} data-testid="profile-avatar">
              <img src={doc.profile_image ? `${BACKEND_URL}${doc.profile_image}` : '/default-avatar-sfoalc.png'} alt={getName()} className="w-full h-full object-cover" />
            </div>
            <div className="text-center sm:text-left">
              <h1 className="text-white text-2xl sm:text-3xl font-bold mb-1" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="profile-name">
                {getName()}
              </h1>
              <p className="text-white/70 text-sm mb-3">Ophtalmologiste adaptateur de lentilles de contact</p>
              <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                {doc.status === 'active' && (
                  <span className="inline-flex items-center gap-1 bg-green-500/80 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Actif
                  </span>
                )}
                {otherLocations.length > 0 && (
                  <span className="inline-flex items-center gap-1 bg-white/15 text-white/90 text-xs font-medium px-3 py-1 rounded-full">
                    <Building2 size={12} /> {otherLocations.length + 1} lieux de consultation
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">

            {/* Contact card */}
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="contact-section">
              <h2 className="text-lg font-bold text-gray-900 mb-5" style={{ fontFamily: "'Barlow', sans-serif" }}>Coordonnées</h2>
              <div className="space-y-4">
                {getAddress() && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#eaf6fc] flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-[#35AFE4]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Adresse du cabinet</p>
                      <p className="text-sm text-gray-800">{getAddress()}</p>
                    </div>
                  </div>
                )}
                {doc.phone && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#eaf6fc] flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-[#35AFE4]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Téléphone</p>
                      <a href={`tel:${doc.phone}`} className="text-sm text-[#35AFE4] font-medium hover:underline">{doc.phone}</a>
                    </div>
                  </div>
                )}
                {doc.website && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#eaf6fc] flex items-center justify-center flex-shrink-0">
                      <Globe size={18} className="text-[#35AFE4]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-0.5">Site web</p>
                      <a href={doc.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#35AFE4] font-medium hover:underline">{doc.website}</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-gray-100">
                {doc.phone && (
                  <a href={`tel:${doc.phone}`} className="flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] text-white rounded-full text-sm font-medium hover:bg-[#2d7d9e] transition-colors" data-testid="cta-call">
                    <Phone size={14} /> Appeler
                  </a>
                )}
                {getLat() && getLng() && (
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${getLat()},${getLng()}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1e3a5f] text-white rounded-full text-sm font-medium hover:bg-[#162d4a] transition-colors" data-testid="cta-itinerary">
                    <Navigation size={14} /> Itinéraire
                  </a>
                )}
                {getLat() && getLng() && (
                  <a href={`https://www.google.com/maps/search/parking/@${getLat()},${getLng()},15z`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 border-2 border-[#3b82f6] text-[#3b82f6] rounded-full text-sm font-medium hover:bg-blue-50 transition-colors" data-testid="cta-parking">
                    <ParkingCircle size={14} /> Parkings
                  </a>
                )}
                {doc.show_contact_button && (
                  <button onClick={() => setShowContactModal(true)} className="flex items-center gap-2 px-5 py-2.5 border-2 border-[#35AFE4] text-[#35AFE4] rounded-full text-sm font-medium hover:bg-[#eaf6fc] transition-colors" data-testid="cta-contact-email">
                    <Mail size={14} /> Contacter par email
                  </button>
                )}
              </div>
            </div>

            {/* Lens types */}
            {lensTypes.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="lens-section">
                <h2 className="text-lg font-bold text-gray-900 mb-5" style={{ fontFamily: "'Barlow', sans-serif" }}>Types de lentilles adaptées</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {lensTypes.map((lens, i) => {
                    const matchKey = Object.keys(lensColors).find(k => lens.toLowerCase().includes(k.toLowerCase()));
                    const colors = lensColors[matchKey] || { bg: '#eaf6fc', text: '#35AFE4' };
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl transition-colors" style={{ background: colors.bg }} data-testid={`lens-tag-${i}`}>
                        <Eye size={18} style={{ color: colors.text }} />
                        <span className="text-sm font-medium" style={{ color: colors.text }}>{lens}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parkings à proximité */}
            {parkings.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="parkings-section">
                <h2 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <ParkingCircle size={20} className="inline mr-2 text-[#3b82f6]" />
                  Parkings à proximité
                </h2>
                <p className="text-sm text-gray-400 mb-4">Dans un rayon de 500m du cabinet</p>
                <div className="space-y-2">
                  {parkings.map((p, i) => (
                    <a key={i}
                      href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-blue-50/50 hover:border-blue-200 transition-all group"
                      data-testid={`parking-${i}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#3b82f6] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-extrabold text-xs">P</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800 group-hover:text-[#3b82f6] transition-colors">{p.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {p.capacity && <span className="text-[10px] text-gray-400">{p.capacity} places</span>}
                          {p.fee === 'yes' && <span className="text-[10px] text-orange-500 font-medium">Payant</span>}
                          {p.fee === 'no' && <span className="text-[10px] text-green-500 font-medium">Gratuit</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[#3b82f6] font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        Itinéraire <ExternalLink size={12} />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Other consultation locations */}
            {otherLocations.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="other-locations-section">
                <h2 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <Building2 size={20} className="inline mr-2 text-[#35AFE4]" />
                  Autres lieux de consultation
                </h2>
                <p className="text-sm text-gray-500 mb-5">Ce praticien consulte également aux adresses suivantes</p>
                <div className="space-y-3">
                  {otherLocations.map((loc, i) => {
                    const targetSlug = loc.slug || loc.id;
                    const goToFiche = (e) => {
                      // Defensive: even if <Link> is somehow blocked, force navigation
                      if (!targetSlug) return;
                      e.preventDefault();
                      navigate(`/annuaire/${targetSlug}`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    };
                    return (
                      <Link
                        key={targetSlug || `loc-${i}`}
                        to={targetSlug ? `/annuaire/${targetSlug}` : '#'}
                        onClick={goToFiche}
                        className="flex items-center gap-4 p-4 rounded-xl border border-[#d0e8f0] hover:bg-[#eaf6fc] hover:border-[#35AFE4]/40 transition-all group cursor-pointer"
                        data-testid={`other-location-${i}`}
                      >
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#e67e22] to-[#d35400] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                          <MapPin size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#1e3a5f] text-sm group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>
                            {loc.name}
                          </p>
                          {loc.address && <p className="text-xs text-gray-400 mt-0.5 truncate"><MapPin size={10} className="inline mr-1" />{loc.address}</p>}
                          {loc.phone && <p className="text-xs text-gray-400 mt-0.5"><Phone size={10} className="inline mr-1" />{loc.phone}</p>}
                        </div>
                        <span className="text-xs text-[#35AFE4] font-semibold flex-shrink-0">
                          Voir la fiche &rarr;
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right column - Map */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-testid="map-section">
              <div ref={mapRef} className="w-full h-64 sm:h-80" />
              <div className="p-4 space-y-2">
                {getLat() && getLng() && (
                  <>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${getLat()},${getLng()}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#1e3a5f] text-white text-sm font-medium rounded-lg hover:bg-[#162d4a] transition-colors"
                      data-testid="google-maps-itinerary"
                    >
                      <Navigation size={14} /> Itinéraire vers le cabinet
                    </a>
                    <a
                      href={`https://www.google.com/maps/search/parking/@${getLat()},${getLng()},15z`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 border border-[#3b82f6] text-[#3b82f6] text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
                      data-testid="google-maps-parking"
                    >
                      <ParkingCircle size={14} /> Trouver un parking
                    </a>
                  </>
                )}
              </div>
            </div>

            {parkings.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <ParkingCircle size={16} className="text-[#3b82f6]" />
                  <span className="text-sm font-bold text-[#3b82f6]">{parkings.length} parking{parkings.length > 1 ? 's' : ''}</span>
                </div>
                <p className="text-xs text-blue-600">trouvé{parkings.length > 1 ? 's' : ''} à proximité du cabinet</p>
              </div>
            )}

            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Fiche praticien</h3>
              <div className="space-y-2 text-xs text-gray-500">
                {doc.created_at && <p>Inscrit depuis le {new Date(doc.created_at).toLocaleDateString('fr-FR')}</p>}
                <p>Référencé dans l'annuaire SFOALC</p>
                {otherLocations.length > 0 && (
                  <p className="text-[#35AFE4] font-medium">{otherLocations.length} autre{otherLocations.length > 1 ? 's' : ''} lieu{otherLocations.length > 1 ? 'x' : ''} de consultation</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #1e3a5f, #35AFE4)' }}>
              <div>
                <h3 className="text-white font-bold text-lg">Contacter {getName()}</h3>
                <p className="text-white/60 text-xs mt-0.5">Votre message sera transmis par email</p>
              </div>
              <button onClick={() => setShowContactModal(false)} className="text-white/70 hover:text-white transition-colors" data-testid="close-contact-modal">
                <X size={20} />
              </button>
            </div>
            {contactSent ? (
              <div className="px-6 py-10 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Send size={24} className="text-green-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">Message envoyé !</h4>
                <p className="text-sm text-gray-500 mb-6">Votre message a bien été transmis à {getName()}.</p>
                <button onClick={() => { setShowContactModal(false); setContactSent(false); setContactForm({ sender_name: '', sender_email: '', message: '' }); }}
                  className="px-6 py-2 bg-[#35AFE4] text-white rounded-full text-sm font-medium hover:bg-[#2d7d9e] transition-colors" data-testid="close-success-modal">
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setContactSending(true);
                try {
                  await axios.post(`${API}/practitioners/${doc.id}/contact`, {
                    practitioner_id: doc.id,
                    ...contactForm,
                  });
                  setContactSent(true);
                } catch (err) {
                  alert(err.response?.data?.detail || 'Erreur lors de l\'envoi');
                } finally {
                  setContactSending(false);
                }
              }} className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Votre nom *</label>
                  <input type="text" required value={contactForm.sender_name} onChange={e => setContactForm(p => ({ ...p, sender_name: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] outline-none transition-all"
                    placeholder="Dr Jean Dupont" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Votre email *</label>
                  <input type="email" required value={contactForm.sender_email} onChange={e => setContactForm(p => ({ ...p, sender_email: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] outline-none transition-all"
                    placeholder="jean.dupont@email.fr" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Votre message *</label>
                  <textarea required value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4] outline-none transition-all resize-none h-28"
                    placeholder="Bonjour, je souhaiterais prendre rendez-vous..." data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={contactSending}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#35AFE4] text-white rounded-lg font-medium text-sm hover:bg-[#2d7d9e] transition-colors disabled:opacity-50"
                  data-testid="send-contact-button">
                  {contactSending ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Send size={16} />}
                  {contactSending ? 'Envoi en cours...' : 'Envoyer le message'}
                </button>
                <p className="text-[10px] text-gray-400 text-center">Votre email ne sera partagé qu'avec ce praticien pour pouvoir vous répondre.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PractitionerProfilePage;
